/**
 * V3 AI Type Definitions
 * Priority-Based CPU with Multi-Ply Minimax
 */

import { Position, Unit, UnitType, UnitCategory, FactionType, City, CityLevel, TerrainType } from '../../types';

// ============================================================================
// GAME STATE SNAPSHOT - Immutable state for search tree
// ============================================================================

export interface UnitSnapshot {
  readonly id: string;
  readonly type: UnitType;
  readonly faction: FactionType;
  readonly position: Position;
  readonly health: number;
  readonly maxHealth: number;
  readonly movementRemaining: number;
  readonly hasAttacked: boolean;
  readonly attack: number;
  readonly defense: number;
  readonly range: number;
  readonly movement: number;
  readonly category: UnitCategory;
  readonly isVeteran: boolean;
  readonly cost: number;
  // Transport state
  readonly isCarryingUnit: boolean;
  readonly towingUnit: boolean;
}

export interface CitySnapshot {
  readonly name: string;
  readonly faction: FactionType | null;
  readonly level: CityLevel;
  readonly centerPosition: Position;
  readonly tiles: ReadonlyArray<Position>;  // All tiles that belong to this city
  readonly isBeingCaptured: boolean;
  readonly capturingFaction: FactionType | null;
  readonly unitsProduced: number;  // Number of units produced by this city
  readonly unitsInTraining: number;  // Number of units currently in training queue
  readonly maxUnits: number;  // Maximum capacity based on city level
  readonly isUpgrading: boolean;  // True if city has upgrade in progress (can't produce units)
}

export interface GameStateSnapshot {
  readonly turn: number;
  readonly currentFaction: FactionType;
  readonly enemyFaction: FactionType;
  readonly units: ReadonlyArray<UnitSnapshot>;
  readonly cities: ReadonlyArray<CitySnapshot>;
  readonly resources: ReadonlyMap<FactionType, number>;
  readonly occupiedPositions: ReadonlySet<string>; // "x,y" format
  readonly mapWidth: number;
  readonly mapHeight: number;
  readonly campaign?: 'normandy' | 'tunisia' | 'stalingrad' | 'okinawa' | 'campobasso' | 'tutorial' | 'france_1940' | 'albania_1940';
  // Unit objectives from FrontManager - each unit's assigned target position
  readonly unitObjectives?: ReadonlyMap<string, Position>;
  // Unit front type assignments from FrontManager - determines behavior (GARRISON units must stay on city)
  readonly unitFrontTypes?: ReadonlyMap<string, FrontType>;
}

// ============================================================================
// FRONT TYPES - Unit assignment categories
// ============================================================================

export enum FrontType {
  ATTACK = 'ATTACK',
  DEFENSE = 'DEFENSE',
  EXPANSION = 'EXPANSION',
  GARRISON = 'GARRISON',  // Hold captured/frontline cities - units should NOT leave
  RESERVE = 'RESERVE'
}

// ============================================================================
// MOVE TYPES - Actions the AI can take
// ============================================================================

export enum MoveType {
  MOVE = 'move',
  ATTACK = 'attack',
  MOVE_AND_ATTACK = 'move_and_attack',
  BUILD = 'build',
  UPGRADE_CITY = 'upgrade_city',
  HUNKER = 'hunker',
  HEAL = 'heal',
  REPAIR = 'repair',
  BOARD_TRANSPORT = 'board_transport',
  DISEMBARK = 'disembark',
  TOW_ARTILLERY = 'tow_artillery',
  UNTOW_ARTILLERY = 'untow_artillery',
  PASS = 'pass'
}

export interface MoveFlags {
  isKillingBlow: boolean;
  threatensCity: boolean;
  capturesCity: boolean;
  retreatsToSafety: boolean;
  supportsAlly: boolean;
  improvesPosition: boolean;
}

export interface GeneratedMove {
  readonly unitId: string;
  readonly type: MoveType;
  readonly from: Position;
  readonly to?: Position;
  readonly targetId?: string;
  readonly priority: number;
  readonly flags: MoveFlags;
  // For production
  readonly unitType?: UnitType;
  readonly cityName?: string;
  readonly upgradeLevel?: CityLevel;
}

// ============================================================================
// AI DECISION - Output format matching legacy AI
// ============================================================================

export interface AIDecision {
  type: 'move' | 'attack' | 'build' | 'upgrade_city' | 'end_turn' | 'hunker' |
        'heal' | 'repair' | 'place_mine' | 'board_transport' | 'disembark' |
        'deploy_paratroopers' | 'tow_artillery' | 'untow_artillery';
  unit?: Unit;
  target?: Position;
  unitType?: UnitType;
  city?: City;
  targetUnit?: Unit;
  upgradeToLevel?: CityLevel;
}

// ============================================================================
// STRATEGIC CONTEXT - Game-level priorities
// ============================================================================

export enum StrategicPriority {
  SURVIVAL = 1,           // Capital/HQ threatened
  CRITICAL_DEFENSE = 2,   // City being captured
  COUNTER_ATTACK = 3,     // Enemy overextended
  EXPANSION = 4,          // Neutral cities reachable
  OFFENSIVE = 5,          // Force ratio favorable
  CONSOLIDATION = 6,      // Need to regroup/heal
  PRODUCTION = 7          // Default: build army
}

export interface StrategicContext {
  readonly priority: StrategicPriority;
  readonly threatenedCities: ReadonlyArray<CitySnapshot>;
  readonly captureTargets: ReadonlyArray<CitySnapshot>;
  readonly vulnerableEnemies: ReadonlyArray<UnitSnapshot>;
  readonly forceRatio: number;
  readonly resourceAdvantage: number;
}

// ============================================================================
// TACTICAL PRIORITIES - Unit-level priorities
// ============================================================================

export enum TacticalPriority {
  KILL_SHOT = 1,      // Can destroy enemy
  SURVIVE = 2,        // Critically damaged and threatened
  PROTECT = 3,        // Ally/city under attack
  OPPORTUNITY = 4,    // Safe favorable attack
  MISSION = 5,        // Strategic objective
  POSITION = 6,       // Improve tactical position
  SUPPORT = 7,        // Heal/repair
  IDLE = 8           // Nothing to do
}

// ============================================================================
// EVALUATION WEIGHTS
// ============================================================================

export const EVALUATION_WEIGHTS = {
  MATERIAL: 1000,
  CITY_CONTROL: 600,
  THREATS: 250,
  POSITION: 400,  // Increased - positional advancement toward objectives is critical
  TEMPO: 100
} as const;

// ============================================================================
// UNIT VALUES - For material evaluation
// ============================================================================

export const UNIT_VALUES: Record<UnitCategory, number> = {
  [UnitCategory.INFANTRY]: 100,
  [UnitCategory.ARMOR]: 250,
  [UnitCategory.ARTILLERY]: 280,
  [UnitCategory.RECONNAISSANCE]: 120,
  [UnitCategory.AIRCRAFT]: 300,
  [UnitCategory.FIGHTERS]: 320,
  [UnitCategory.BOMBERS]: 350,
  [UnitCategory.TRANSPORT_AIRCRAFT]: 150,
  [UnitCategory.ENGINEERS]: 150,
  [UnitCategory.MEDICS]: 150,
  [UnitCategory.TRANSPORT]: 80,
  [UnitCategory.HALFTRACK]: 100
};

// ============================================================================
// TERRAIN DATA
// ============================================================================

export interface TerrainData {
  readonly movementCost: number;
  readonly defenseBonus: number;
  readonly attackPenalty: number;
}

export const TERRAIN_DATA: Record<TerrainType, TerrainData> = {
  // Urban (cities)
  [TerrainType.URBAN]: { movementCost: 1, defenseBonus: 0.3, attackPenalty: 0 },

  // Easy terrain (cost 1)
  [TerrainType.OPEN_FIELDS]: { movementCost: 1, defenseBonus: 0, attackPenalty: 0 },
  [TerrainType.DESERT_PLAINS]: { movementCost: 1, defenseBonus: 0, attackPenalty: 0 },
  [TerrainType.FROZEN_STEPPE]: { movementCost: 1, defenseBonus: 0, attackPenalty: 0 },
  [TerrainType.RICE_PADDIES]: { movementCost: 1, defenseBonus: 0, attackPenalty: 0 },
  [TerrainType.FRENCH_COUNTRYSIDE]: { movementCost: 1, defenseBonus: 0, attackPenalty: 0 },
  [TerrainType.VALLEY_FLOORS]: { movementCost: 1, defenseBonus: 0, attackPenalty: 0 },
  [TerrainType.COASTAL_LOWLANDS]: { movementCost: 1, defenseBonus: 0, attackPenalty: 0 },

  // Medium terrain (cost 2, +20% defense)
  [TerrainType.BOCAGE]: { movementCost: 2, defenseBonus: 0.2, attackPenalty: 0 },
  [TerrainType.ROCKY_HIGHLANDS]: { movementCost: 2, defenseBonus: 0.2, attackPenalty: 0 },
  [TerrainType.SNOW_DRIFTS]: { movementCost: 2, defenseBonus: 0.2, attackPenalty: 0 },
  [TerrainType.CORAL_RIDGES]: { movementCost: 2, defenseBonus: 0.2, attackPenalty: 0 },
  [TerrainType.ROLLING_HILLS]: { movementCost: 2, defenseBonus: 0.2, attackPenalty: 0 },
  [TerrainType.WOODED_HILLS]: { movementCost: 2, defenseBonus: 0.2, attackPenalty: 0 },
  [TerrainType.ALBANIAN_HIGHLANDS]: { movementCost: 2, defenseBonus: 0.2, attackPenalty: 0 },

  // Hard terrain (cost 3, +20% defense, -10% attack)
  [TerrainType.DENSE_WOODS]: { movementCost: 3, defenseBonus: 0.2, attackPenalty: 0.1 },
  [TerrainType.SHIFTING_DUNES]: { movementCost: 3, defenseBonus: 0.2, attackPenalty: 0.1 },
  [TerrainType.FROZEN_MARSHES]: { movementCost: 3, defenseBonus: 0.2, attackPenalty: 0.1 },
  [TerrainType.DENSE_JUNGLE]: { movementCost: 3, defenseBonus: 0.2, attackPenalty: 0.1 },
  [TerrainType.DENSE_FORESTS]: { movementCost: 3, defenseBonus: 0.2, attackPenalty: 0.1 },
  [TerrainType.MOUNTAIN_RIDGES]: { movementCost: 3, defenseBonus: 0.2, attackPenalty: 0.1 },
  [TerrainType.PINDUS_MOUNTAINS]: { movementCost: 3, defenseBonus: 0.2, attackPenalty: 0.1 }
};

// ============================================================================
// SEARCH CONFIGURATION
// ============================================================================

export interface SearchConfig {
  readonly baseDepth: number;
  readonly maxDepth: number;
  readonly timeLimit: number;  // milliseconds
  readonly maxMovesPerUnit: number;
}

export function getSearchConfig(activeUnitCount: number): SearchConfig {
  if (activeUnitCount <= 5) {
    return { baseDepth: 6, maxDepth: 10, timeLimit: 5000, maxMovesPerUnit: 15 };
  } else if (activeUnitCount <= 10) {
    return { baseDepth: 4, maxDepth: 7, timeLimit: 4000, maxMovesPerUnit: 12 };
  } else if (activeUnitCount <= 15) {
    return { baseDepth: 3, maxDepth: 5, timeLimit: 3000, maxMovesPerUnit: 10 };
  } else {
    return { baseDepth: 2, maxDepth: 4, timeLimit: 2000, maxMovesPerUnit: 8 };
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function positionToKey(pos: Position): string {
  return `${pos.x},${pos.y}`;
}

export function keyToPosition(key: string): Position {
  const [x, y] = key.split(',').map(Number);
  return { x, y };
}

/**
 * Calculate distance between two positions on a square grid (Chebyshev distance)
 * Note: Despite the name "hexDistance", this game uses square tiles
 */
export function hexDistance(a: Position, b: Position): number {
  // Chebyshev distance for square grid - allows diagonal movement
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/**
 * Get all 8 adjacent positions for a square grid
 * Note: Despite the name suggesting hex, this game uses square tiles
 */
export function getAdjacentPositions(pos: Position): Position[] {
  // Square grid has 8 neighbors (including diagonals)
  return [
    { x: pos.x - 1, y: pos.y - 1 }, // NW
    { x: pos.x,     y: pos.y - 1 }, // N
    { x: pos.x + 1, y: pos.y - 1 }, // NE
    { x: pos.x - 1, y: pos.y },     // W
    { x: pos.x + 1, y: pos.y },     // E
    { x: pos.x - 1, y: pos.y + 1 }, // SW
    { x: pos.x,     y: pos.y + 1 }, // S
    { x: pos.x + 1, y: pos.y + 1 }  // SE
  ];
}

/**
 * Check if a position is on any tile of a city
 */
export function isPositionOnCity(pos: Position, city: CitySnapshot): boolean {
  return city.tiles.some(tile => tile.x === pos.x && tile.y === pos.y);
}

/**
 * Find the closest city tile to a position
 */
export function getClosestCityTile(pos: Position, city: CitySnapshot): Position {
  let closest = city.tiles[0];
  let closestDist = hexDistance(pos, closest);

  for (const tile of city.tiles) {
    const dist = hexDistance(pos, tile);
    if (dist < closestDist) {
      closestDist = dist;
      closest = tile;
    }
  }

  return closest;
}

/**
 * Get distance to the closest tile of a city
 */
export function distanceToCity(pos: Position, city: CitySnapshot): number {
  let minDist = Infinity;
  for (const tile of city.tiles) {
    const dist = hexDistance(pos, tile);
    if (dist < minDist) {
      minDist = dist;
    }
  }
  return minDist;
}

/**
 * Check if a unit category can capture cities
 * ALL ground units can capture, but AIRCRAFT cannot
 */
export function canCategoryCapture(category: UnitCategory): boolean {
  // Aircraft categories cannot capture cities
  return category !== UnitCategory.AIRCRAFT &&
         category !== UnitCategory.FIGHTERS &&
         category !== UnitCategory.BOMBERS &&
         category !== UnitCategory.TRANSPORT_AIRCRAFT;
}

/**
 * Check if a unit can capture cities (convenience wrapper)
 */
export function canUnitCapture(unit: UnitSnapshot): boolean {
  return canCategoryCapture(unit.category);
}
